import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { AnalyticsContext } from '@/analytics/constants';
import { trackSelectItem } from '@/analytics/ecommerce';
import Text from '@/components/cms/Text';
import DotSeparatedContent, { DotSeparator } from '@/components/ui/DotSeparatedContent';
import Link from '@/components/ui/Link';
import Thumbnail, { ThumbnailProps } from '@/components/ui/Thumbnail';
import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';
import { useAddToCart } from '@/hooks/shared/useAddToCart';
import { useLaunch } from '@/hooks/shared/useLaunch';
import classnames from 'classnames';
import React, { HTMLAttributeAnchorTarget, useCallback, useMemo } from 'react';

export interface SidePanelResultCardProps {
  index?: number;
  showIndexOverlay?: boolean;
  heading?: string;
  labelDetail?: string;
  labelInfo?: string;
  image?: {
    url: string;
    alt?: string;
  };
  formats?: Array<string> | null;
  productLink?: { url?: string; target?: HTMLAttributeAnchorTarget };
  userCanLaunch?: boolean;
  launchUrl?: string;
  variationPk: string;
  itemAnalyticsModel?: ItemAnalyticsModel;
  context?: AnalyticsContext;
  creditTrackerCode?: string;
  listId?: string;
  listName?: string;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
  clickOnCardContent?: (card: Record<string, unknown>) => void;
  thumbnail?: Partial<Omit<ThumbnailProps, 'image' | 'onClick'>>;
  extraActions?: React.ReactNode;
  /** use natural language and names for formats instead of simple comma-separated list  */
  useNaturalPhrasing?: boolean;
  variant?: 'default' | 'credit-plan';
}

const SidePanelResultCard: React.FC<SidePanelResultCardProps> = ({
  heading,
  labelDetail,
  labelInfo,
  image,
  productLink,
  formats,
  variationPk,
  userCanLaunch,
  launchUrl,
  itemAnalyticsModel,
  context,
  clickOnCardContent,
  creditTrackerCode,
  index,
  listId,
  listName,
  itemListAnalyticsModel,
  thumbnail: thumbnailProps,
  extraActions,
  useNaturalPhrasing,
  variant = 'default'
}) => {
  // only used for naturalPhrasing
  const { liveEcomLabels } = useCatalogLabels(!useNaturalPhrasing);

  const containerClasses = classnames('flex items-start justify-between');
  const headingClasses = classnames('text-2-medium mb-3 block lg:mb-4', {
    'text-link cursor-pointer': productLink?.url,
    'sm:text-1-medium': variant === 'credit-plan'
  });
  const subheadingClasses = classnames('text-2 mt-2 w-full text-gray-dark', {
    'md:text-1-fixed': variant === 'credit-plan'
  });

  const imageSizes = useMemo(() => {
    return formats?.every(a => a.toLowerCase() === 'on-demand')
      ? {
          xs: '327px',
          md: '352px',
          lg: '628px'
        }
      : undefined;
  }, [formats]);

  const imageIcon = useMemo(() => {
    if (userCanLaunch && productLink?.url) {
      return {
        name: 'play'
      };
    }

    if (!userCanLaunch && variationPk) {
      return {
        name: 'cart'
      };
    }

    return undefined;
  }, [productLink?.url, userCanLaunch, variationPk]);

  const formatsLabel = useMemo(() => {
    if (!useNaturalPhrasing || !formats) return formats?.join(', ');

    const formatsNames = formats.map(f => {
      switch (f.toLowerCase()) {
        case 'live webcast':
        case 'webcast':
        case 'online':
          return liveEcomLabels.online;
        case 'live in-person':
        case 'live in person':
        case 'in-person':
        case 'in person':
          return liveEcomLabels.inPerson;
        case 'live groupcast':
        case 'groupcast':
          return liveEcomLabels.groupcast;
        default:
          return f;
      }
    });

    return new Intl.ListFormat('en-us', {
      style: 'long',
      type: 'disjunction'
    }).format(formatsNames);
  }, [
    formats,
    liveEcomLabels.groupcast,
    liveEcomLabels.inPerson,
    liveEcomLabels.online,
    useNaturalPhrasing
  ]);

  const analyticsContext = useMemo(() => {
    return context ?? AnalyticsContext.CreditTracker;
  }, [context]);

  const { launch } = useLaunch(analyticsContext, creditTrackerCode, itemListAnalyticsModel);
  const { addToCart } = useAddToCart(analyticsContext, creditTrackerCode, itemListAnalyticsModel);

  const trackEvent = useCallback(() => {
    trackSelectItem(
      itemAnalyticsModel,
      analyticsContext,
      listId,
      listName,
      itemListAnalyticsModel,
      typeof index === 'number' && index >= 0 ? index : undefined
    );
  }, [analyticsContext, index, itemAnalyticsModel, itemListAnalyticsModel, listId, listName]);

  const onThumbnailClick = useCallback(() => {
    if (userCanLaunch && productLink?.url) {
      launch({
        code: variationPk,
        isLiveUnregLaunch: false,
        alreadyPurchasedLaunchUrl: launchUrl,
        itemAnalyticsModel,
        listId,
        listName,
        index: typeof index === 'number' && index >= 0 ? index : undefined
      });
    } else if (variationPk && !userCanLaunch) {
      addToCart({ code: variationPk });
    }
  }, [
    userCanLaunch,
    launch,
    variationPk,
    launchUrl,
    itemAnalyticsModel,
    listId,
    listName,
    index,
    addToCart,
    productLink?.url
  ]);

  const handleCardClick = useCallback(() => {
    if (clickOnCardContent) {
      clickOnCardContent({ variationPk, heading });
    } else {
      trackEvent();
    }
  }, [clickOnCardContent, trackEvent, variationPk, heading]);

  return (
    <div className={containerClasses} data-component={'SidePanelResultCard'}>
      <div className="mr-3 w-3/4">
        {heading &&
          (productLink?.url ? (
            <Link
              href={productLink.url}
              target={productLink.target ?? '__blank'}
              className={headingClasses}
              text={heading}
              variant="none"
              onClick={handleCardClick}
            />
          ) : (
            <Text propertyName="h5" element="h5" className={headingClasses}>
              {heading}
            </Text>
          ))}
        <div className={subheadingClasses}>
          {formats?.length && labelDetail ? (
            <DotSeparatedContent content={[formatsLabel, labelDetail]} />
          ) : (
            <Text propertyName="span" element="span">
              {formatsLabel ?? labelDetail}
            </Text>
          )}
          <Text
            propertyName="span"
            element="span"
            className="text-2 block text-gray-dark @sm700:inline-block"
          >
            <span className="hidden @sm700:inline">
              &nbsp;
              <DotSeparator />{' '}
            </span>
            {labelInfo}
          </Text>
        </div>
        {!!extraActions && (
          <div className="mt-3 flex flex-col items-baseline gap-2">{extraActions}</div>
        )}
      </div>
      <div className="w-1/4">
        <Thumbnail
          ratio={1}
          image={image}
          icon={imageIcon}
          mini={true}
          onClick={onThumbnailClick}
          imageSizes={imageSizes}
          {...thumbnailProps}
        />
      </div>
    </div>
  );
};

export default SidePanelResultCard;
